import { Component } from '@theme/component';

/**
 * A site-wide notification card that appears after a delay and can be dismissed.
 *
 * Once the user closes it or clicks the CTA, a session cookie is set so the card
 * stays hidden for the rest of the browsing session.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} closeButton - The close button.
 *
 * @extends {Component<Refs>}
 */
class NotificationPopup extends Component {
  requiredRefs = ['closeButton'];

  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    super.connectedCallback();

    // Always show in the theme editor so the merchant can see and style it.
    if (window.Shopify?.designMode) {
      this.#show();
      return;
    }

    // Respect a previous dismissal for the rest of the session.
    if (this.#isDismissed()) return;

    const delay = Number(this.dataset.delay) || 0;
    this.#timer = window.setTimeout(() => this.#show(), delay);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#timer) clearTimeout(this.#timer);
  }

  /**
   * Closes the notification and remembers the dismissal for the session.
   */
  close() {
    this.#hide();
    this.#setDismissCookie();
  }

  /**
   * Called when the CTA is clicked. Remembers the dismissal but lets the
   * link navigation proceed as normal.
   */
  dismissOnDownload() {
    this.#setDismissCookie();
  }

  #show() {
    this.classList.add('notification--visible');
    this.setAttribute('aria-hidden', 'false');
  }

  #hide() {
    this.classList.remove('notification--visible');
    this.setAttribute('aria-hidden', 'true');
  }

  get #cookieName() {
    return this.dataset.cookieName || 'twp_notification_dismissed';
  }

  #isDismissed() {
    return document.cookie.split('; ').some((cookie) => cookie.startsWith(`${this.#cookieName}=`));
  }

  #setDismissCookie() {
    // No `max-age`/`expires` makes this a session cookie: it's cleared when the
    // browser session ends, so the notification can return on a fresh visit.
    document.cookie = `${this.#cookieName}=1; path=/; SameSite=Lax`;
  }
}

if (!customElements.get('notification-popup')) {
  customElements.define('notification-popup', NotificationPopup);
}
